import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, AlertCircle, Info, CheckCheck, Send } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  high: { color: "text-[#FF3B30]", border: "border-l-[#FF3B30]", icon: AlertTriangle, label: "TINGGI" },
  medium: { color: "text-[#FFCC00]", border: "border-l-[#FFCC00]", icon: AlertCircle, label: "SEDANG" },
  low: { color: "text-[#00F0FF]", border: "border-l-[#00F0FF]", icon: Info, label: "INFO" },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: unreadOnly } });
      setItems(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unreadOnly]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    load();
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`${data.updated} notifikasi ditandai dibaca`);
      load();
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts/send-digest");
      const em = data.results?.email;
      const tg = data.results?.telegram;
      let msg = `Digest: ${data.notification_count} alert. `;
      if (em?.status === "sent") msg += "Email ✓ ";
      else if (em?.status === "skipped") msg += "Email skipped ";
      if (tg?.status === "sent") msg += "Telegram ✓";
      else if (tg?.status === "skipped") msg += "Telegram skipped";
      toast.success(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal kirim digest");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 fade-in">
      <div className="pb-2 border-b border-[#1F1F1F] flex items-end justify-between">
        <div>
          <div className="label-mono">// alert stream</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="notifications-title">
            Pusat <span className="text-[#00F0FF]">Notifikasi</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            {unreadCount > 0 ? (
              <span><span className="text-[#FF3B30] font-mono">{unreadCount}</span> notifikasi belum dibaca</span>
            ) : "Semua notifikasi sudah dibaca."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendDigest} disabled={busy}
            data-testid="btn-send-digest"
            className="label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#00F0FF] hover:text-[#00F0FF] flex items-center gap-2">
            <Send size={12} /> kirim digest
          </button>
          <button onClick={markAllRead} disabled={busy || unreadCount === 0}
            data-testid="btn-mark-all-read"
            className="label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#00E676] hover:text-[#00E676] disabled:opacity-30 flex items-center gap-2">
            <CheckCheck size={12} /> tandai semua dibaca
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 border border-[#1F1F1F] w-fit" data-testid="notif-filter-tabs">
        <button onClick={() => setUnreadOnly(false)}
          data-testid="notif-filter-all"
          className={`label-mono px-4 py-2 ${!unreadOnly ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}>
          semua
        </button>
        <button onClick={() => setUnreadOnly(true)}
          data-testid="notif-filter-unread"
          className={`label-mono px-4 py-2 ${unreadOnly ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"}`}>
          belum dibaca ({unreadCount})
        </button>
      </div>

      <div className="max-w-4xl">
        {loading ? (
          <div className="label-mono text-zinc-500"><span className="terminal-cursor">memuat</span></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#1F1F1F] p-16 text-center">
            <div className="label-mono text-[#00E676] mb-2">// all clear</div>
            <p className="text-zinc-400">
              {unreadOnly ? "Tidak ada notifikasi belum dibaca." : "Tidak ada notifikasi saat ini."}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="notifications-list">
            {items.map((n) => {
              const s = SEV[n.severity] || SEV.low;
              const Icon = s.icon;
              return (
                <div key={n.id}
                  data-testid={`notification-${n.id}`}
                  className={`border border-[#1F1F1F] border-l-4 ${s.border} bg-[#0A0A0A] px-5 py-4 flex items-start gap-4 ${n.read ? "opacity-50" : ""}`}>
                  <Icon size={18} className={s.color} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`label-mono ${s.color}`}>{s.label}</span>
                      <span className="label-mono text-zinc-600">·</span>
                      <span className="label-mono text-zinc-500">{n.channel}</span>
                      {!n.read && <span className="label-mono text-[#00F0FF]">● baru</span>}
                    </div>
                    <p className="text-sm text-white">{n.message}</p>
                    <div className="label-mono mt-2">{new Date(n.created_at).toLocaleString("id-ID")}</div>
                  </div>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)}
                      data-testid={`mark-read-${n.id}`}
                      className="label-mono px-3 py-1.5 border border-[#1F1F1F] hover:border-[#00E676] hover:text-[#00E676] shrink-0">
                      tandai dibaca
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
